/**
 * „Aus Karte übernehmen?" (Mockup freigegeben von Antoine am 13.10.2026, „Nur vorschlagen").
 *
 * Eine Speise-/Getränkezeile ohne Kartenbezug (von Hand getippt, aus einem alten Angebot) zeigt
 * „Allergene fehlen". Passt ihr Name EINDEUTIG zu einem Karteneintrag derselben Art, der seine
 * Allergene gepflegt hat, schlägt die Zeile die Verknüpfung vor — verknüpft wird erst auf Klick.
 * Die Treffer-Regel ist dieselbe wie beim Text-Einfügen auf dem Server
 * (`@maestro/katalog-treffer`). Name und Preis der Zeile bleiben beim Übernehmen unverändert.
 */
import { useSyncExternalStore } from "react";
import { findeKatalogTreffer } from "@maestro/katalog-treffer";
import { kindToSection } from "./builder-helpers";
import type { CatalogItem, OfferItem } from "../ui/types";

type Lmiv = { allergens: string[] | null; allergensCheckedAt?: string | null };

/** LMIV-Lücke: keine Datenbasis, ungepflegt oder ungeprüfter Alt-Bestand (dieselbe Bedingung wie die Prüfliste). */
export function allergeneFehlen(l: Lmiv | null | undefined): boolean {
  return !l || l.allergens == null || (l.allergens.length === 0 && l.allergensCheckedAt == null);
}

/** Der eindeutige Karteneintrag zum Namen einer unverknüpften Speise/Getränk-Zeile (oder null). */
export function kartenTreffer(item: OfferItem, katalog: readonly CatalogItem[]): CatalogItem | null {
  if (item.catalogItemId != null || (item.section !== "menu" && item.section !== "drinks")) return null;
  return findeKatalogTreffer(
    item.name,
    katalog.filter((ci) => ci.archivedAt == null && kindToSection(ci.kind) === item.section),
  );
}

/** Vorschlag nur, wenn der Treffer die Lücke auch schließt — sonst bringt die Verknüpfung nichts. */
export function kartenVorschlag(item: OfferItem, katalog: readonly CatalogItem[]): CatalogItem | null {
  const t = kartenTreffer(item, katalog);
  return t && !allergeneFehlen(t) ? t : null;
}

/* „Nein" blendet den Vorschlag für diese Zeile aus — Zeile UND Prüfliste lesen denselben Stand.
   ponytail: nur im Speicher (bis zum Neuladen); dauerhaft erst, wenn Antoine das verlangt. */
let abgelehnt = new Set<string>();
const hoerer = new Set<() => void>();
export function vorschlagAblehnen(itemId: string): void {
  abgelehnt = new Set(abgelehnt).add(itemId);
  hoerer.forEach((h) => h());
}
export function useAbgelehnteVorschlaege(): ReadonlySet<string> {
  return useSyncExternalStore(
    (h) => {
      hoerer.add(h);
      return () => hoerer.delete(h);
    },
    () => abgelehnt,
  );
}
